'use client';

import { c as _c } from "react/compiler-runtime";
import { animated, useSpring } from '@react-spring/web';
import { useMemo, useState } from 'react';
import { vectorConfigs } from './vector-config.js';
const useAnimatedStyle = (length, toggle, animationType) => {
  const $ = _c(20);
  const t0 = toggle ? 0 : length;
  let t1;
  if ($[0] !== length || $[1] !== t0) {
    t1 = {
      strokeDasharray: length,
      strokeDashoffset: t0
    };
    $[0] = length;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const baseConfig = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      duration: 800,
      mass: 1,
      tension: 280,
      friction: 120
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== baseConfig) {
    t3 = {
      ...baseConfig,
      config: t2,
      delay: 1000
    };
    $[4] = baseConfig;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      duration: 800,
      mass: 1,
      tension: 280,
      friction: 120
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== baseConfig) {
    t5 = {
      ...baseConfig,
      config: t4,
      delay: 200
    };
    $[7] = baseConfig;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      duration: 8000,
      mass: 1,
      tension: 280,
      friction: 120
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== baseConfig) {
    t7 = {
      ...baseConfig,
      config: t6,
      delay: 1000
    };
    $[10] = baseConfig;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      duration: 3000,
      mass: 1,
      tension: 280,
      friction: 120
    };
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== baseConfig) {
    t9 = {
      ...baseConfig,
      config: t8,
      delay: 1000
    };
    $[13] = baseConfig;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t3 || $[16] !== t5 || $[17] !== t7 || $[18] !== t9) {
    t10 = {
      default: t3,
      trans: t5,
      long: t7,
      medium: t9
    };
    $[15] = t3;
    $[16] = t5;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const configs = t10;
  return useSpring(configs[animationType] || configs.default);
};
const AnimatedPath = t0 => {
  const $ = _c(7);
  const {
    d,
    stroke,
    style,
    setLength
  } = t0;
  let t1;
  if ($[0] !== setLength) {
    t1 = ref => {
      if (ref) {
        setLength(ref.getTotalLength());
      }
    };
    $[0] = setLength;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== d || $[3] !== stroke || $[4] !== style || $[5] !== t1) {
    t2 = <animated.path style={style} ref={t1} d={d} stroke={stroke} />;
    $[2] = d;
    $[3] = stroke;
    $[4] = style;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
const VectorSvg = t0 => {
  const $ = _c(8);
  const {
    viewBox,
    children,
    className,
    preserveAspectRatio: t1,
    hidden
  } = t0;
  const preserveAspectRatio = t1 === undefined ? "xMinYMin meet" : t1;
  const t2 = hidden ? "hidden" : "visible";
  let t3;
  if ($[0] !== t2) {
    t3 = {
      visibility: t2
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== className || $[4] !== preserveAspectRatio || $[5] !== t3 || $[6] !== viewBox) {
    t4 = <svg viewBox={viewBox} fill="none" preserveAspectRatio={preserveAspectRatio} xmlns="http://www.w3.org/2000/svg" className={className} style={t3}>{children}</svg>;
    $[2] = children;
    $[3] = className;
    $[4] = preserveAspectRatio;
    $[5] = t3;
    $[6] = viewBox;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
};
function AnimateLines(t0) {
  const $ = _c(10);
  const {
    id,
    toggle,
    className
  } = t0;
  const [length, setLength] = useState(null);
  const config = vectorConfigs[id];
  const animationType = config?.animationType || (id.includes("_mob") ? "trans" : "default");
  const ready = length != null;
  const animatedStyle = useAnimatedStyle(length, toggle, animationType);
  if (!config) {
    return null;
  }
  const t1 = !ready;
  let t2;
  if ($[0] !== animatedStyle || $[1] !== config.d || $[2] !== config.stroke) {
    t2 = <AnimatedPath d={config.d} stroke={config.stroke} style={animatedStyle} setLength={setLength} />;
    $[0] = animatedStyle;
    $[1] = config.d;
    $[2] = config.stroke;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== className || $[5] !== config.preserveAspectRatio || $[6] !== config.viewBox || $[7] !== t1 || $[8] !== t2) {
    t3 = <VectorSvg viewBox={config.viewBox} className={className} preserveAspectRatio={config.preserveAspectRatio} hidden={t1}>{t2}</VectorSvg>;
    $[4] = className;
    $[5] = config.preserveAspectRatio;
    $[6] = config.viewBox;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
export default AnimateLines;